import { Action, ActionType } from 'typesafe-actions';
import { ofType } from 'redux-observable';
import { Observable, of, OperatorFunction, from } from 'rxjs';
import { catchError, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { toReduxError } from '@universal/errors';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { fromApiRequest } from '@shared/apiClient/fromApiRequest';
import { CarMeta } from '@appTypes/carModelType';
import { Actions, CarResponse, actions } from '@redux/modules/cars';
import { TypedEpic } from './types';

const getOneCarEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_ONE_CAR),
    withLatestFrom(state$) as unknown as OperatorFunction<Action<any>, ActionType<typeof actions.getOneCar.request>[]>,
    switchMap(([action]: { payload: string }[]) => {
      const params = { slugs: action.payload };

      return fromApiRequest((signal) =>
        apiClient.get<CarResponse>(ApiType.apimVehicleDataApi, {
          signal,
          url: ApiPaths[ApiType.apimVehicleDataApi].models,
          params,
        }),
      ).pipe(
        map((response) => actions.getOneCar.success(response.data, { slug: action.payload })),
        catchError((err) =>
          of(actions.getOneCar.failure(toReduxError(err, 'Could not get car'), { slug: action.payload })),
        ),
      );
    }),
  );

const getTestedCarsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_TESTED_CARS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getTestedCars.request>[]
    >,
    switchMap(() =>
      fromApiRequest((signal) =>
        apiClient.get<CarResponse>(ApiType.apimVehicleDataApi, {
          signal,
          url: ApiPaths[ApiType.apimVehicleDataApi].models,
        }),
      ).pipe(
        map((response) => actions.getTestedCars.success(response.data)),
        catchError((err) => of(actions.getTestedCars.failure(toReduxError(err, 'Could not get tested cars')))),
      ),
    ),
  );

const getCarModelMetaEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_CAR_MODEL_META),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getCarModelMeta.request>[]
    >,
    mergeMap(([action]: { payload: string; meta: { slug: string } }[]) =>
      from(
        apiClient.get<CarMeta>(ApiType.apimNafNoApi, {
          // Note: This uses a custom endpoint for vehicle model articles
          url: `vehiclemodelarticles/${action.payload}`,
        }),
      ).pipe(
        map((response) => actions.getCarModelMeta.success(response.data, { slug: action.meta.slug })),
        catchError((err) =>
          of(
            actions.getCarModelMeta.failure(toReduxError(err, 'Could not enrich car model'), {
              slug: action.meta.slug,
            }),
          ),
        ),
      ),
    ),
  );

export const fetchCarsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_CARS),
    withLatestFrom(state$) as unknown as OperatorFunction<Action<any>, ActionType<typeof actions.getCars.request>[]>,
    switchMap(([action]: { payload: string }[]) => {
      // Parse the query string from the payload
      const searchParams = new URLSearchParams(action.payload);
      const params = Object.fromEntries(searchParams.entries());

      return fromApiRequest((signal) =>
        apiClient.get<CarResponse>(ApiType.apimVehicleDataApi, {
          signal,
          url: ApiPaths[ApiType.apimVehicleDataApi].models,
          params,
        }),
      ).pipe(
        map((response) => actions.getCars.success(response.data)),
        catchError((err) => of(actions.getCars.failure(toReduxError(err, 'Could not get cars')))),
      );
    }),
  );

const fetchMoreCarsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_MORE_CARS),
    withLatestFrom(state$) as unknown as OperatorFunction<
      Action<any>,
      ActionType<typeof actions.getMoreCars.request>[]
    >,
    switchMap(([action]: { payload: string }[]) => {
      // Parse the query string from the payload
      const searchParams = new URLSearchParams(action.payload);
      const params = Object.fromEntries(searchParams.entries());

      return fromApiRequest((signal) =>
        apiClient.get<CarResponse>(ApiType.apimVehicleDataApi, {
          signal,
          url: ApiPaths[ApiType.apimVehicleDataApi].models,
          params,
        }),
      ).pipe(
        map((response) => actions.getMoreCars.success(response.data)),
        catchError((err) => of(actions.getMoreCars.failure(toReduxError(err, 'Could not get more cars')))),
      );
    }),
  );

export const epics: TypedEpic[] = [
  getOneCarEpic,
  fetchCarsEpic,
  fetchMoreCarsEpic,
  getCarModelMetaEpic,
  getTestedCarsEpic,
];
